/**
 * OAuth provider whose Kanbu-owned client credentials back a connector preset.
 * Maps to `config('mcpConnectors.<oauthProfile>')`.
 */
export type McpOAuthProfile =
  | 'google'
  | 'microsoft'
  | 'notion'
  | 'linear'
  | 'freelo';

export type McpConnectorCategory =
  | 'productivity'
  | 'dev'
  | 'payments'
  | 'workspace';

/** `on_request` = not self-serve yet; ask us to enable it for your org. */
export type McpConnectorStatus =
  | 'available'
  | 'beta'
  | 'planned'
  | 'on_request';

/**
 * A curated, one-click connector preset. Picking one pre-fills the integration
 * form with a known server URL + OAuth profile so the org only has to Connect.
 */
export interface McpConnectorPreset {
  id: string;
  name: string;
  description: string;
  category: McpConnectorCategory;
  serverUrl: string;
  /** OAuth scopes requested against Kanbu's pre-registered client. */
  scopes: string[];
  oauthProfile: McpOAuthProfile;
  docsUrl?: string;
  status: McpConnectorStatus;
}

/**
 * First-party connector presets (Phase 3C). Kanbu registers one OAuth app per
 * provider; each org connects its own account via the provider consent screen.
 *
 * Google Drive requests `drive.readonly`, a *restricted* scope — Google
 * requires an annual CASA security assessment before an app using it can
 * leave OAuth Testing mode. Until we go through that, this connector is
 * `on_request`: enabled per-org manually (org added as an OAuth test user),
 * not offered self-serve.
 */
export const mcpConnectorCatalog: McpConnectorPreset[] = [
  {
    id: 'notion',
    name: 'Notion',
    description: 'Search and update Notion pages and databases.',
    category: 'productivity',
    serverUrl: 'https://mcp.notion.com/mcp',
    scopes: [],
    oauthProfile: 'notion',
    docsUrl: 'https://developers.notion.com/guides/mcp/get-started-with-mcp',
    status: 'available',
  },
  {
    id: 'linear',
    name: 'Linear',
    description: 'Create and update Linear issues and projects.',
    category: 'dev',
    serverUrl: 'https://mcp.linear.app/mcp',
    scopes: [],
    oauthProfile: 'linear',
    docsUrl: 'https://linear.app/docs',
    status: 'available',
  },
  {
    id: 'freelo',
    name: 'Freelo',
    description: 'Manage Freelo tasks, projects, and time tracking.',
    category: 'productivity',
    serverUrl: 'https://mcp.freelo.io/mcp',
    scopes: [],
    oauthProfile: 'freelo',
    docsUrl: 'https://www.freelo.io/en/help/freelo-mcp',
    status: 'available',
  },
  {
    id: 'google-drive',
    name: 'Google Drive',
    description:
      'Search, read, and create Drive files (app-scoped files only).',
    category: 'workspace',
    serverUrl: 'https://drivemcp.googleapis.com/mcp/v1',
    scopes: [
      'https://www.googleapis.com/auth/drive.file',
      'https://www.googleapis.com/auth/drive.readonly',
    ],
    oauthProfile: 'google',
    docsUrl:
      'https://developers.google.com/workspace/drive/api/guides/configure-mcp-server',
    // drive.readonly is a restricted scope (Google CASA review required for
    // production) — not self-serve yet, enable per-org on request.
    status: 'on_request',
  },
  {
    id: 'microsoft-365',
    name: 'Microsoft 365',
    description: 'Connect Microsoft 365 (requires tenant admin consent).',
    category: 'workspace',
    serverUrl: '',
    scopes: [],
    oauthProfile: 'microsoft',
    docsUrl:
      'https://learn.microsoft.com/en-us/power-pages/configure/mcp-connect-clients',
    status: 'on_request',
  },
];
